import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Plus, Star, Check, X } from 'lucide-react'
import { Label } from '../ui/label'
import type { ImageData } from '../../types/images'

interface ImageGalleryEditorProps {
  images: ImageData[]
  onImagesChange: (images: ImageData[]) => void
  cachedImageUrl?: string // Fallback cached image URL
  imageUrl?: string // Fallback original image URL
  sourceUrl?: string // Product source URL for linking
  title?: string // Product title for alt text
  isEditable?: boolean // Whether images can be selected/edited
  onAddImage?: () => void // Callback when add image button is clicked
  maxRecommended?: number // Maximum recommended images (default 3)
}

export function ImageGalleryEditor({
  images = [],
  onImagesChange,
  cachedImageUrl,
  imageUrl,
  sourceUrl,
  title = '',
  isEditable = true,
  onAddImage,
  maxRecommended = 3
}: ImageGalleryEditorProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<number>>(new Set())

  // Reset image errors when images change
  useEffect(() => {
    setImageLoadErrors(new Set())
    // Reset to first image if current index is out of bounds
    if (currentImageIndex >= images.length && images.length > 0) {
      setCurrentImageIndex(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length])

  // Get selected images for display (if editable mode)
  const selectedImages = isEditable
    ? images.filter(img => img.selected)
    : images

  // Determine which images to show in carousel
  // Note: We don't sort here - sorting happens on save/load, not inline during editing
  const imagesToShow = selectedImages.length > 0 ? selectedImages : images

  // Get the current image URL with priority: cached > original
  const getCurrentImageUrl = (img: ImageData | undefined, index: number): string | null => {
    if (!img) return null

    // Priority: cached URLs over external URLs
    const url = img.cached_url ||
      (index === 0 ? cachedImageUrl : null) ||
      img.url ||
      img.src ||
      (index === 0 ? imageUrl : null)

    return url || null
  }

  // Handle image selection toggle
  const handleImageToggle = (index: number) => {
    if (!isEditable) return

    const updatedImages = images.map((image, i) =>
      i === index ? { ...image, selected: !image.selected } : image
    )
    onImagesChange(updatedImages)
  }

  // Handle setting featured/highlighted image
  const handleSetFeatured = (index: number, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isEditable) return

    const updatedImages = images.map((image, i) => ({
      ...image,
      highlighted: i === index,
      // Auto-select when setting as featured
      selected: i === index ? true : image.selected
    }))
    onImagesChange(updatedImages)
  }

  // Navigate carousel
  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentImageIndex(prev =>
      prev === 0 ? imagesToShow.length - 1 : prev - 1
    )
  }

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentImageIndex(prev =>
      prev === imagesToShow.length - 1 ? 0 : prev + 1
    )
  }

  // Handle image load error
  const handleImageError = (index: number) => {
    setImageLoadErrors(prev => new Set(prev).add(index))
  }

  // Render the main image display area
  const renderMainImage = () => {
    if (imagesToShow.length === 0 && isEditable) {
      // Empty state for add mode
      return (
        <div className="relative group">
          <div
            className="w-full bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-300"
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 200px)', aspectRatio: '4/5' }}
          >
            <Plus className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-400 text-sm text-center px-6" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              Add photos to help others discover this product
            </p>
            {onAddImage && (
              <button
                onClick={onAddImage}
                className="mt-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
              >
                Add Image
              </button>
            )}
          </div>
        </div>
      )
    }

    if (imagesToShow.length === 0) {
      // Fallback for view mode with no images
      const fallbackUrl = cachedImageUrl || imageUrl
      if (fallbackUrl) {
        return (
          <div className="relative group">
            {sourceUrl ? (
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                <img
                  src={fallbackUrl}
                  alt={title || ''}
                  className="w-full object-contain rounded-lg"
                  style={{ maxHeight: 'calc(100vh - 200px)' }}
                  onError={() => handleImageError(0)}
                />
              </a>
            ) : (
              <img
                src={fallbackUrl}
                alt={title || ''}
                className="w-full object-contain rounded-lg"
                style={{ maxHeight: 'calc(100vh - 200px)' }}
                onError={() => handleImageError(0)}
              />
            )}
          </div>
        )
      }

      return (
        <div className="relative group">
          <div
            className="w-full bg-gray-100 rounded-lg flex items-center justify-center"
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 200px)', aspectRatio: '4/5' }}
          >
            <p className="text-gray-400 text-sm" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              No images available
            </p>
          </div>
        </div>
      )
    }

    const currentImage = imagesToShow[currentImageIndex]
    const currentImageUrl = getCurrentImageUrl(currentImage, currentImageIndex)

    // Check if current image failed to load
    if (imageLoadErrors.has(currentImageIndex)) {
      return (
        <div className="relative group">
          <div
            className="w-full bg-gray-100 rounded-lg flex items-center justify-center"
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 200px)', aspectRatio: '4/5' }}
          >
            <p className="text-gray-400 text-sm" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              Failed to load image
            </p>
          </div>
        </div>
      )
    }

    if (!currentImageUrl) {
      return (
        <div className="relative group">
          <div
            className="w-full bg-gray-100 rounded-lg flex items-center justify-center"
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 200px)', aspectRatio: '4/5' }}
          >
            <p className="text-gray-400 text-sm" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              Image pending
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className="relative group">
        {sourceUrl && !isEditable ? (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
            <img
              src={currentImageUrl}
              alt={currentImage?.alt || title || ''}
              className="w-full object-contain rounded-lg"
              style={{ maxHeight: 'calc(100vh - 200px)' }}
              onError={() => handleImageError(currentImageIndex)}
            />
          </a>
        ) : (
          <img
            src={currentImageUrl}
            alt={currentImage?.alt || title || ''}
            className="w-full object-contain rounded-lg"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
            onError={() => handleImageError(currentImageIndex)}
          />
        )}

        {/* Star indicator for featured image on main image */}
        {isEditable && imagesToShow.length > 0 && (
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              // Find the original index of the current image in the full images array
              const currentImg = imagesToShow[currentImageIndex]
              const originalIndex = images.findIndex(img =>
                (img.cached_url || img.url) === (currentImg?.cached_url || currentImg?.url)
              )
              if (originalIndex !== -1) {
                handleSetFeatured(originalIndex, e)
              }
            }}
            className="group/star absolute top-3 left-3 flex items-center gap-2 p-1.5 bg-white/90 hover:bg-white rounded-full transition-all shadow-md z-20"
            type="button"
          >
            {currentImage?.highlighted ? (
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
            ) : (
              <Star className="h-5 w-5 text-gray-400 group-hover/star:text-yellow-500 transition-colors flex-shrink-0" />
            )}
            <span
              className="text-xs font-medium text-gray-600 whitespace-nowrap opacity-0 w-0 group-hover/star:opacity-100 group-hover/star:w-auto group-hover/star:pr-1.5 transition-all duration-200"
              style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
            >
              featured image
            </span>
          </button>
        )}

        {/* Navigation Arrows */}
        {imagesToShow.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 shadow-md opacity-0 group-hover:opacity-100 transition-opacity z-10"
              type="button"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 shadow-md opacity-0 group-hover:opacity-100 transition-opacity z-10"
              type="button"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Image counter dots */}
        {imagesToShow.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {imagesToShow.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setCurrentImageIndex(index)
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-gray-800 w-6'
                    : 'bg-gray-400 hover:bg-gray-600'
                }`}
                type="button"
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Render thumbnail selection grid
  const renderThumbnailGrid = () => {
    if (!isEditable || images.length === 0) return null

    return (
      <div className="mt-6">
        <Label style={{ fontFamily: "'GT America Mono', 'Courier New', monospace", fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Select Images to Save
        </Label>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {images.map((img, index) => {
            const imageUrl = img.cached_url || img.url || img.src || ''

            // Skip invalid images
            if (!imageUrl || imageUrl === '?' || imageUrl === '') {
              return null
            }

            return (
              <div
                key={index}
                onClick={() => handleImageToggle(index)}
                className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                  img.selected
                    ? 'border-[#57241F] ring-2 ring-[#57241F] ring-offset-2'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <img
                  src={imageUrl}
                  alt={`Option ${index + 1}`}
                  className="w-full h-20 object-cover"
                  onError={(e) => {
                    const imgElement = e.currentTarget
                    imgElement.style.display = 'none'
                    const errorDiv = document.createElement('div')
                    errorDiv.className = 'w-full h-20 bg-gray-100 flex items-center justify-center'
                    errorDiv.innerHTML = '<span class="text-xs text-gray-400">Failed to load</span>'
                    imgElement.parentElement?.insertBefore(errorDiv, imgElement.parentElement.firstChild)
                  }}
                />

                {/* Featured indicator (display only, not clickable) */}
                {img.highlighted && (
                  <div className="absolute top-1 left-1 p-0.5 bg-white/90 rounded-full shadow-sm">
                    <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                  </div>
                )}

                {/* Selection indicator */}
                {img.selected ? (
                  <div className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow-sm">
                    <Check className="h-3 w-3 text-[#57241F]" />
                  </div>
                ) : (
                  <div className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow-sm opacity-50">
                    <X className="h-3 w-3 text-gray-400" />
                  </div>
                )}
              </div>
            )
          })}

          {/* Add Image Button */}
          {onAddImage && (
            <button
              onClick={onAddImage}
              className="relative rounded-lg overflow-hidden border-2 border-dashed border-gray-300 hover:border-gray-400 transition-all h-20 flex items-center justify-center bg-gray-50 hover:bg-gray-100"
              type="button"
            >
              <Plus className="h-6 w-6 text-gray-400" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
          Click images to select/deselect (max {maxRecommended} recommended)
        </p>
      </div>
    )
  }

  return (
    <div>
      {renderMainImage()}
      {renderThumbnailGrid()}
    </div>
  )
}
